<template>
  <form @submit.prevent="validateAndSubmit">
    <div class="row">
      <div class="col col-md-8 col-12">
        <div class="form-group">
          <label for="careplanTitle">Prescription Name</label>
          <b-form-input
            v-model="intervention.name"
            :disabled="disabled"
            v-validate="'required'"
            :class="{'danger': errors.has('careplanTitle') }"
            name="careplanTitle"
            type="text"
            placeholder="eg. Outdoor walk, STIVARGA"
          />
        </div>
      </div>
      <div class="col col-md-4 col-12">
        <div class="form-group">
          <v-select
            :disabled="disabled"
            :options="colors"
            :labelAttrs="{
              style: {
                backgroundColor: intervention.color,
                color: 'white'
              },
              class:'color-label',
            }"
            :onSelect="setColor"
            value=""
            label="Label Colour"
          />
        </div>
      </div>
      <div class="col col-md-8 col-12">
        <div class="form-group">
          <label for="category">Category</label>
          <b-form-select
            v-model="intervention.category_id"
            :disabled="disabled"
            v-validate="'required'"
            :class="{'danger': errors.has('category_id') }"
            :options="interventionCategoryOptions"
            name="category_id"
          />
        </div>
      </div>
      <div class="col col-md-4 col-12">
        <div class="form-group">
          <label
            class="col-12 p-0"
            for="dosage"
          >Dosage</label>
          <b-form-input
            v-model="intervention.dosage"
            :disabled="disabled"
            v-validate="'required'"
            :class="{'danger': errors.has('dosage') }"
            name="dosage"
            type="text"
            placeholder="eg.40mg"
          />
        </div>
      </div>
      <div class="col col-md-6 col-12">
        <div class="form-group">
          <label
            class="col-12 p-0"
            for="days"
          >Every</label>
          <b-form-select
            v-model="intervention.skip_days"
            :disabled="disabled"
            v-validate="'required'"
            :class="{'danger': errors.has('days') }"
            :options="days"
            name="days"
          />
        </div>
      </div>
      <div class="col col-md-6 col-12">
        <div class="form-group">
          <label
            class="col-12 p-0"
            for="frequency"
          >Frequency</label>
          <b-form-select
            v-model="intervention.frequency"
            :disabled="disabled"
            v-validate="'required'"
            :class="{'danger': errors.has('frequency') }"
            :options="frequencyOptions"
            name="frequency"
          />
        </div>
      </div>
      <div class="col col-12 reminder-content">
        <div
          v-for="(frequency, index) in reminders"
          :key="index"
          class="form-group reminders m-0"
        >
          <v-time-picker
            :class="`m-0`"
            :disabled="disabled"
            @onChange="(value) => onReminderChange(value, index)"
            :label="`Reminder ${index + 1}`"
            :value="reminders[index]"
            :minTime="reminders[index -1] || '0'"
            :className="{'danger': errors.has(`reminder${index + 1}`) }"
            :name="`reminder${index + 1}`"
            v-validate="`required|unique:${`reminder${index + 1}`},${index},${reminders}`"
          />
        </div>
      </div>
      <div class="col col-md-12 col-12 error-text">
        <template v-for="(frequency, index) in reminders">
          <template v-if="!errors.first(`reminder${index + 1 -1}`) || index === 0">
            {{ errors.first(`reminder${index + 1}`) }}
          </template>
        </template>
      </div>
      <div class="col col-12">
        <div class="form-group">
          <label
            class="col-12 p-0"
            for="instructions"
          >Instructions</label>
          <textarea
            v-model="intervention.instructions"
            :disabled="disabled"
            v-validate="'required'"
            :class="{'danger': errors.has('instructions') }"
            class="form-control"
            name="instructions"
            placeholder="instructions"
            rows="3"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import actions from '@/constants/_actions';
import careplan from '@/mixins/careplan';

const everyDay = 'Everyday';
export default {
  name: 'Intervention',
  mixins: [careplan],
  props: {
    data: {
      type: Object,
      required: true
    },
    module: {
      type: String,
      required: true
    },
    actionType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      intervention: this.data,
      reminders: this.clone(this.data.reminders) || [],
      everyDay,
      showDays: false,
      selectedDays: [],
      selectedColorName: '',
      colors: [
        {
          label: 'Blue',
          value: '#00759A',
          attrs: {
            style: {
              backgroundColor: '#00759A'
            }
          }
        },
        {
          label: 'Orange',
          value: '#F7941D',
          attrs: {
            style: {
              backgroundColor: '#F7941D'
            }
          }
        },
        {
          label: 'Red',
          value: '#A71930',
          attrs: {
            style: {
              backgroundColor: '#A71930'
            }
          }
        },
        {
          label: 'Green',
          value: '#679146',
          attrs: {
            style: {
              backgroundColor: '#679146'
            }
          }
        }
      ],
      days: this.getDaysList(1, 7),
      frequencyOptions: [
        { value: '1', text: '1' },
        { value: '2', text: '2' },
        { value: '3', text: '3' },
        { value: '4', text: '4' },
        { value: '5', text: '5' }
      ]
    };
  },
  computed: {
    daysCount() {
      return this.selectedDays.length;
    },
    disabled() {
      return this.actionType === actions.VIEW;
    }
  },
  watch: {
    'data.frequency': 'handleReminders'
  },
  methods: {
    handleReminders() {
      const { reminders } = this;
      const frequency = parseInt(this.data.frequency, 10);
      while (reminders.length !== frequency) {
        if ((reminders.length < frequency)) {
          reminders.push('');
        } else {
          reminders.pop();
        }
      }
      this.data.reminders = reminders;
      this.reminders = reminders;
    },
    setColor(color) {
      this.intervention.color = color;
    },
    validateAndSubmit() {
      this.$validator.validateAll().then((result) => {
        this.intervention.careplan_id = this.$store.state.careplan.careplan.id;
        if (result) {
          this.$emit('onSubmit', this.intervention);
        }
      });
    },
    toggleDayDropDown(value = !this.showDays) {
      this.showDays = value;
    },
    selectDays(days) {
      this.intervention.total_days = days.length;
      this.intervention.days = days;
    },
    onReminderChange(value, index) {
      this.reminders[index] = value;
      this.intervention.reminders = this.reminders;
    },
    getDaysList() {
      const days = [{ value: 1, text: 'day' }];
      for (let i = 2; i <= 6; i += 1) {
        days.push({ value: i, text: `${i} days` });
      }
      days.push({ value: 7, text: 'week' });
      days.push({ value: 14, text: '2 weeks' });
      days.push({ value: 21, text: '3 weeks' });
      return days;
    }
  }
};
</script>
<style lang="scss" scoped>
  @import './style.scss';
</style>
